/**
 * SharePointBrowserModal
 *
 * Modal fullscreen avec navigation source-based pour parcourir:
 * - OneDrive personnel
 * - Fichiers partagés
 * - Sites SharePoint (avec toutes leurs bibliothèques de documents)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Text,
  Spinner,
  Input,
  Checkbox,
  Card,
  MessageBar,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Toolbar,
  ToolbarButton,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
  TableCellLayout,
  TableColumnDefinition,
} from '@fluentui/react-components';
import {
  DismissRegular,
  SearchRegular,
  FolderRegular,
  DocumentRegular,
  ArrowUpRegular,
  CheckmarkCircleRegular,
  CloudArrowUp24Regular,
  People24Regular,
  Globe24Regular,
  Library24Regular,
} from '@fluentui/react-icons';

import { microsoftGraphService } from '@/services/microsoftGraphService';
import { SharePointFileInfo } from '@/types/api';
import { Site } from '@microsoft/microsoft-graph-types';

// Configured SharePoint site from env
const CONFIGURED_SP_HOSTNAME = import.meta.env.VITE_SP_HOSTNAME || '';
const CONFIGURED_SP_SITE_PATH = import.meta.env.VITE_SP_SITE_PATH || '';

// --- Type definitions ---

type NavigationLevel = 'sources' | 'onedrive' | 'shared' | 'site-drives' | 'drive-browse';

interface NavigationContext {
  level: NavigationLevel;
  label: string;
  siteId?: string;
  siteName?: string;
  driveId?: string;
  driveName?: string;
  folderId?: string;
}

interface VirtualFolderItem {
  id: string;
  name: string;
  description?: string;
  icon: 'onedrive' | 'shared' | 'site' | 'library';
}

interface FileItem extends SharePointFileInfo {
  selected: boolean;
}

interface SharePointBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (files: SharePointFileInfo[]) => void;
  multiSelect?: boolean;
}

// --- Helper ---

const getVirtualFolderIcon = (icon: string) => {
  switch (icon) {
    case 'onedrive': return <CloudArrowUp24Regular />;
    case 'shared': return <People24Regular />;
    case 'site': return <Globe24Regular />;
    case 'library': return <Library24Regular />;
    default: return <FolderRegular />;
  }
};

// --- Component ---

const SharePointBrowserModal: React.FC<SharePointBrowserModalProps> = ({
  isOpen,
  onClose,
  onFilesSelected,
  multiSelect = true
}) => {
  const initialContext: NavigationContext = { level: 'sources', label: 'Sources' };

  // Navigation state
  const [navigationStack, setNavigationStack] = useState<NavigationContext[]>([initialContext]);
  const [currentContext, setCurrentContext] = useState<NavigationContext>(initialContext);

  // Data state
  const [virtualFolders, setVirtualFolders] = useState<VirtualFolderItem[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [discoveredSites, setDiscoveredSites] = useState<Site[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Race condition guard
  const navigationCounterRef = useRef(0);

  // --- Initialization ---

  useEffect(() => {
    if (isOpen) {
      initializeSources();
    } else {
      // Reset on close
      setNavigationStack([initialContext]);
      setCurrentContext(initialContext);
      setFiles([]);
      setVirtualFolders([]);
      setSelectedFiles(new Set());
      setSearchQuery('');
      setError(null);
      setDiscoveredSites([]);
    }
  }, [isOpen]);

  const initializeSources = async () => {
    setIsLoading(true);
    setError(null);

    // Test connection
    const ok = await microsoftGraphService.testConnection();
    if (!ok) {
      setError("Problème d'authentification avec Microsoft Graph. Veuillez vous reconnecter.");
      setIsLoading(false);
      return;
    }

    // Build source list
    const sources: VirtualFolderItem[] = [
      { id: 'onedrive', name: 'Mon OneDrive', description: 'Fichiers personnels', icon: 'onedrive' },
      { id: 'shared', name: 'Partagés avec moi', description: 'Fichiers partagés par d\'autres utilisateurs', icon: 'shared' },
    ];

    const allSites: Site[] = [];
    const siteIds = new Set<string>();

    // Resolve configured site first (deterministic)
    if (CONFIGURED_SP_HOSTNAME && CONFIGURED_SP_SITE_PATH) {
      try {
        const configuredSite = await microsoftGraphService.getConfiguredSite(
          CONFIGURED_SP_HOSTNAME,
          CONFIGURED_SP_SITE_PATH
        );
        if (configuredSite?.id) {
          allSites.push(configuredSite);
          siteIds.add(configuredSite.id);
          sources.push({
            id: configuredSite.id,
            name: configuredSite.displayName || 'SharePoint',
            description: configuredSite.webUrl || '',
            icon: 'site',
          });
        }
      } catch (err) {
        console.warn('[Sources] Could not resolve configured site:', err);
      }
    }

    // Discover additional sites (non-blocking)
    try {
      const additionalSites = await microsoftGraphService.getSites();
      for (const site of additionalSites) {
        if (site.id && !siteIds.has(site.id)) {
          allSites.push(site);
          siteIds.add(site.id);
          sources.push({
            id: site.id,
            name: site.displayName || site.name || 'Site SharePoint',
            description: site.webUrl || '',
            icon: 'site',
          });
        }
      }
    } catch (err) {
      console.warn('[Sources] Site discovery failed (non-fatal):', err);
    }

    setDiscoveredSites(allSites);
    setVirtualFolders(sources);
    setFiles([]);
    setIsLoading(false);
  };

  // --- Navigation ---

  const navigateTo = async (context: NavigationContext, newStack?: NavigationContext[]) => {
    const myNavigation = ++navigationCounterRef.current;
    setIsLoading(true);
    setError(null);
    setSelectedFiles(new Set());
    setSearchQuery('');

    try {
      switch (context.level) {
        case 'sources': {
          await initializeSources();
          // initializeSources handles its own state updates
          if (navigationCounterRef.current !== myNavigation) return;
          setCurrentContext(context);
          if (newStack) setNavigationStack(newStack);
          setIsLoading(false);
          return;
        }

        case 'onedrive': {
          const items = await microsoftGraphService.getDriveItems(undefined, context.folderId);
          if (navigationCounterRef.current !== myNavigation) return;
          const transformed = microsoftGraphService.transformMultipleToSharePointFileInfo(items);
          setVirtualFolders([]);
          setFiles(transformed.map(f => ({ ...f, selected: false })));
          break;
        }

        case 'shared': {
          const sharedItems = await microsoftGraphService.getSharedWithMe();
          if (navigationCounterRef.current !== myNavigation) return;
          const transformed = microsoftGraphService.transformMultipleToSharePointFileInfo(sharedItems);
          setVirtualFolders([]);
          setFiles(transformed.map(f => ({ ...f, selected: false })));
          break;
        }

        case 'site-drives': {
          const drives = await microsoftGraphService.getSiteDrives(context.siteId!);
          if (navigationCounterRef.current !== myNavigation) return;
          const driveItems: VirtualFolderItem[] = drives.map(drive => ({
            id: drive.id!,
            name: drive.name || 'Bibliothèque',
            description: drive.driveType || '',
            icon: 'library' as const,
          }));
          setVirtualFolders(driveItems);
          setFiles([]);
          break;
        }

        case 'drive-browse': {
          const items = await microsoftGraphService.getDriveItemsByDriveId(
            context.driveId!,
            context.folderId
          );
          if (navigationCounterRef.current !== myNavigation) return;
          const transformed = microsoftGraphService.transformMultipleToSharePointFileInfo(items);
          setVirtualFolders([]);
          setFiles(transformed.map(f => ({ ...f, selected: false })));
          break;
        }
      }

      if (navigationCounterRef.current !== myNavigation) return;
      setCurrentContext(context);
      if (newStack) setNavigationStack(newStack);
    } catch (err) {
      if (navigationCounterRef.current !== myNavigation) return;
      console.error('[Navigation] Error:', err);
      setError('Erreur lors du chargement: ' + (err as Error).message);
    } finally {
      if (navigationCounterRef.current === myNavigation) {
        setIsLoading(false);
      }
    }
  };

  const handleVirtualFolderClick = (item: VirtualFolderItem) => {
    let newContext: NavigationContext;

    if (item.id === 'onedrive') {
      newContext = { level: 'onedrive', label: 'Mon OneDrive' };
    } else if (item.id === 'shared') {
      newContext = { level: 'shared', label: 'Partagés avec moi' };
    } else if (item.icon === 'site') {
      newContext = {
        level: 'site-drives',
        label: item.name,
        siteId: item.id,
        siteName: item.name,
      };
    } else if (item.icon === 'library') {
      newContext = {
        level: 'drive-browse',
        label: item.name,
        siteId: currentContext.siteId,
        siteName: currentContext.siteName,
        driveId: item.id,
        driveName: item.name,
      };
    } else {
      return;
    }

    const newStack = [...navigationStack, newContext];
    navigateTo(newContext, newStack);
  };

  const handleFolderNavigation = (folder: FileItem) => {
    if (!folder.is_folder) return;

    let newContext: NavigationContext;

    if (currentContext.level === 'onedrive') {
      newContext = {
        level: 'onedrive',
        label: folder.name,
        folderId: folder.id,
      };
    } else if (currentContext.level === 'drive-browse') {
      newContext = {
        level: 'drive-browse',
        label: folder.name,
        siteId: currentContext.siteId,
        siteName: currentContext.siteName,
        driveId: currentContext.driveId,
        driveName: currentContext.driveName,
        folderId: folder.id,
      };
    } else {
      // Shared item folder — use its driveId
      const driveId = folder.driveId;
      if (!driveId) return;
      newContext = {
        level: 'drive-browse',
        label: folder.name,
        driveId: driveId,
        folderId: folder.id,
      };
    }

    const newStack = [...navigationStack, newContext];
    navigateTo(newContext, newStack);
  };

  const handleGoUp = () => {
    if (navigationStack.length <= 1) return;
    const newStack = navigationStack.slice(0, -1);
    const parentContext = newStack[newStack.length - 1];
    navigateTo(parentContext, newStack);
  };

  const handleBreadcrumbNavigation = (index: number) => {
    if (index === navigationStack.length - 1) return;
    const newStack = navigationStack.slice(0, index + 1);
    const targetContext = newStack[newStack.length - 1];
    navigateTo(targetContext, newStack);
  };

  // --- Search ---

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setIsLoading(true);
    setError(null);

    try {
      let results: any[] = [];

      switch (currentContext.level) {
        case 'onedrive':
          results = await microsoftGraphService.searchFiles(searchQuery);
          break;
        case 'drive-browse':
          if (currentContext.driveId) {
            results = await microsoftGraphService.searchInDrive(searchQuery, currentContext.driveId);
          }
          break;
        case 'site-drives':
          if (currentContext.siteId) {
            results = await microsoftGraphService.searchFiles(searchQuery, currentContext.siteId);
          }
          break;
        default:
          results = await microsoftGraphService.searchFiles(searchQuery);
          break;
      }

      const transformed = microsoftGraphService.transformMultipleToSharePointFileInfo(results);
      setFiles(transformed.map(f => ({ ...f, selected: false })));
      setVirtualFolders([]);
      setSelectedFiles(new Set());
    } catch (err) {
      setError('Erreur lors de la recherche');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, currentContext]);

  // --- File selection ---

  const handleFileSelection = (fileId: string, selected: boolean) => {
    const newSelectedFiles = new Set(selectedFiles);

    if (selected) {
      if (multiSelect) {
        newSelectedFiles.add(fileId);
      } else {
        newSelectedFiles.clear();
        newSelectedFiles.add(fileId);
      }
    } else {
      newSelectedFiles.delete(fileId);
    }

    setSelectedFiles(newSelectedFiles);
    setFiles(prev => prev.map(file => ({
      ...file,
      selected: newSelectedFiles.has(file.id)
    })));
  };

  const handleSelectAll = (checked: boolean) => {
    if (!multiSelect) return;

    const selectableFiles = files.filter(file => !file.is_folder);
    const newSelectedFiles = checked
      ? new Set(selectableFiles.map(file => file.id))
      : new Set<string>();

    setSelectedFiles(newSelectedFiles);
    setFiles(prev => prev.map(file => ({
      ...file,
      selected: checked && !file.is_folder
    })));
  };

  const handleConfirmSelection = () => {
    const selected = files.filter(file => selectedFiles.has(file.id));
    const selectedFileInfos: SharePointFileInfo[] = selected.map(file => ({
      id: file.id,
      name: file.name,
      size: file.size,
      driveId: file.driveId || currentContext.driveId,
      path: file.path,
      is_folder: file.is_folder,
      web_url: file.web_url,
      created_at: file.created_at,
      modified_at: file.modified_at,
    }));

    onFilesSelected(selectedFileInfos);
  };

  // --- DataGrid columns ---

  const columns: TableColumnDefinition<FileItem>[] = [
    createTableColumn<FileItem>({
      columnId: 'selection',
      renderHeaderCell: () => (
        multiSelect ? (
          <Checkbox
            checked={files.filter(f => !f.is_folder).length > 0 &&
                     files.filter(f => !f.is_folder).every(f => selectedFiles.has(f.id))}
            onChange={(_, data) => handleSelectAll(data.checked === true)}
          />
        ) : null
      ),
      renderCell: (item) => (
        item.is_folder ? null : (
          <Checkbox
            checked={selectedFiles.has(item.id)}
            onChange={(_, data) => handleFileSelection(item.id, data.checked === true)}
          />
        )
      ),
    }),
    createTableColumn<FileItem>({
      columnId: 'name',
      compare: (a, b) => a.name.localeCompare(b.name),
      renderHeaderCell: () => 'Nom',
      renderCell: (item) => (
        <TableCellLayout
          media={item.is_folder ? <FolderRegular /> : <DocumentRegular />}
        >
          {item.is_folder ? (
            <Button
              appearance="transparent"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleFolderNavigation(item); }}
            >
              {item.name}
            </Button>
          ) : (
            item.name
          )}
        </TableCellLayout>
      ),
    }),
    createTableColumn<FileItem>({
      columnId: 'size',
      compare: (a, b) => (a.size || 0) - (b.size || 0),
      renderHeaderCell: () => 'Taille',
      renderCell: (item) => (
        item.is_folder ? '-' : `${Math.round((item.size || 0) / 1024)} KB`
      ),
    }),
    createTableColumn<FileItem>({
      columnId: 'modified',
      renderHeaderCell: () => 'Modifié',
      renderCell: (item) => (
        item.modified_at
          ? new Date(item.modified_at).toLocaleDateString('fr-FR')
          : '-'
      ),
    }),
  ];

  // --- Render ---

  if (!isOpen) return null;

  const showSearchBar = currentContext.level !== 'sources';

  return (
    <Dialog open={isOpen} modalType="alert">
      <DialogSurface style={{
        width: '90vw',
        maxWidth: '1200px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <DialogTitle style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e1e1e1',
          background: '#f8f9fa',
          margin: 0,
          padding: '16px 24px'
        }}>
          Parcourir SharePoint
          <Button
            appearance="transparent"
            icon={<DismissRegular />}
            onClick={onClose}
          />
        </DialogTitle>

        <DialogBody style={{
          padding: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        }}>
          {/* Toolbar */}
          <div style={{ padding: '12px 24px', borderBottom: '1px solid #e1e1e1' }}>
            <Toolbar>
              <ToolbarButton
                icon={<ArrowUpRegular />}
                onClick={handleGoUp}
                disabled={navigationStack.length <= 1}
              >
                Niveau supérieur
              </ToolbarButton>

              {showSearchBar && (
                <div style={{ flex: 1, margin: '0 16px' }}>
                  <Input
                    placeholder="Rechercher des fichiers..."
                    value={searchQuery}
                    onChange={(_, data) => setSearchQuery(data.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    contentAfter={
                      <Button
                        appearance="transparent"
                        icon={<SearchRegular />}
                        onClick={handleSearch}
                      />
                    }
                  />
                </div>
              )}
            </Toolbar>
          </div>

          {/* Breadcrumbs */}
          <div style={{ padding: '8px 24px', borderBottom: '1px solid #e1e1e1' }}>
            <Breadcrumb>
              {navigationStack.map((ctx, index) => (
                <React.Fragment key={`${ctx.level}-${index}`}>
                  <BreadcrumbItem>
                    <BreadcrumbButton
                      onClick={() => handleBreadcrumbNavigation(index)}
                      current={index === navigationStack.length - 1}
                    >
                      {ctx.label}
                    </BreadcrumbButton>
                  </BreadcrumbItem>
                  {index < navigationStack.length - 1 && <BreadcrumbDivider />}
                </React.Fragment>
              ))}
            </Breadcrumb>
          </div>

          {/* Main content area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            {error && (
              <MessageBar intent="error" style={{ margin: '16px 24px 0' }}>
                {error}
              </MessageBar>
            )}

            {isLoading ? (
              <div style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <Spinner />
                <Text>Chargement...</Text>
              </div>
            ) : (
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '16px 24px',
                minHeight: 0,
              }}>
                {/* Virtual folders (sources, drives) */}
                {virtualFolders.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: files.length > 0 ? '16px' : 0 }}>
                    {virtualFolders.map(vf => (
                      <Card
                        key={vf.id}
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleVirtualFolderClick(vf); }}
                        style={{ cursor: 'pointer', padding: '12px 16px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {getVirtualFolderIcon(vf.icon)}
                          <div>
                            <Text weight="semibold">{vf.name}</Text>
                            {vf.description && (
                              <Text size={200} style={{ display: 'block', color: '#666' }}>
                                {vf.description}
                              </Text>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {/* File/folder listing */}
                {files.length > 0 && (
                  <DataGrid
                    items={files}
                    columns={columns}
                    sortable
                    getRowId={(item) => item.id}
                  >
                    <DataGridHeader>
                      <DataGridRow>
                        {({ renderHeaderCell }) => (
                          <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                        )}
                      </DataGridRow>
                    </DataGridHeader>
                    <DataGridBody<FileItem>>
                      {({ item, rowId }) => (
                        <DataGridRow<FileItem> key={rowId}>
                          {({ renderCell }) => (
                            <DataGridCell>{renderCell(item)}</DataGridCell>
                          )}
                        </DataGridRow>
                      )}
                    </DataGridBody>
                  </DataGrid>
                )}

                {/* Empty state */}
                {virtualFolders.length === 0 && files.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <Text>Aucun élément à afficher</Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogActions style={{
          padding: '16px 24px',
          borderTop: '1px solid #e1e1e1',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f8f9fa'
        }}>
          <Text size={300}>
            {selectedFiles.size > 0
              ? `${selectedFiles.size} fichier(s) sélectionné(s)`
              : 'Aucun fichier sélectionné'
            }
          </Text>

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              appearance="secondary"
              onClick={onClose}
            >
              Annuler
            </Button>
            <Button
              appearance="primary"
              disabled={selectedFiles.size === 0}
              onClick={handleConfirmSelection}
              icon={<CheckmarkCircleRegular />}
            >
              Sélectionner ({selectedFiles.size})
            </Button>
          </div>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

export default SharePointBrowserModal;
